import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, Users, Building2, TrendingUp } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import { useLanguage } from '../contexts/LanguageContext';
import { tamData, objectifs, TAM_TOTAL } from '../mock';

export default function MarchePage() {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Badge Partie */}
      <div className="mb-4 flex items-center gap-2 text-sm max-w-5xl mx-auto">
        <span className="w-3 h-3 bg-purple-500 rounded-sm"></span>
        <span className="text-purple-400 font-semibold">PARTIE I – STRATÉGIE DE MARCHÉ</span>
        <span className="text-gray-500 ml-2">Page 2 de 3</span>
      </div>
      
      <div className="max-w-5xl mx-auto">
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
              {language === 'fr' ? 'Marché — Écoles' : 'Market — Schools'}
            </CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* TAM Cards - 10 PROVINCES */}
            <div>
              <h3 className="text-xl font-bold text-lime-400 mb-4">{language === 'fr' ? 'Marchés Totaux Adressables (TAM) — 10 Provinces' : 'Total Addressable Markets (TAM) — 10 Provinces'}</h3>
              
              {/* 4 GRANDES PROVINCES */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* QUÉBEC */}
                <Card className="bg-gradient-to-br from-lime-500/20 to-lime-500/5 border-lime-500/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-6 w-6 text-lime-400" />
                      <CardTitle className="text-white text-lg">{language === 'fr' ? 'Québec' : 'Quebec'}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-lime-400">{tamData.qc.total.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'écoles totales' : 'total schools'}</p>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Public:</span>
                        <span className="text-lime-300 font-semibold">{tamData.qc.public.toLocaleString('fr-CA')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">{language === 'fr' ? 'Privé' : 'Private'}:</span>
                        <span className="text-lime-300 font-semibold">{tamData.qc.prive}</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-lime-500/20">
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'Objectif' : 'Target'}</p>
                      <p className="text-xl font-bold text-lime-400">{objectifs.qc.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-lime-300">~{Math.round(objectifs.qc / tamData.qc.total * 100)}%</p>
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-gray-500">Population</p>
                      <p className="text-sm font-semibold text-gray-300">525k {language === 'fr' ? 'élèves' : 'students'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* ONTARIO */}
                <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border-cyan-500/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-6 w-6 text-cyan-400" />
                      <CardTitle className="text-white text-lg">Ontario</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-cyan-400">{tamData.on.total.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'écoles totales' : 'total schools'}</p>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Public:</span>
                        <span className="text-cyan-300 font-semibold">{tamData.on.public.toLocaleString('fr-CA')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">{language === 'fr' ? 'Privé' : 'Private'}:</span>
                        <span className="text-cyan-300 font-semibold">{tamData.on.prive}</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-cyan-500/20">
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'Objectif' : 'Target'}</p>
                      <p className="text-xl font-bold text-cyan-400">{objectifs.on.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-cyan-300">~{Math.round(objectifs.on / tamData.on.total * 100)}%</p>
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-gray-500">Population</p>
                      <p className="text-sm font-semibold text-gray-300">1.2M {language === 'fr' ? 'élèves' : 'students'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* ALBERTA */}
                <Card className="bg-gradient-to-br from-orange-500/20 to-orange-500/5 border-orange-500/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-6 w-6 text-orange-400" />
                      <CardTitle className="text-white text-lg">Alberta</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-orange-400">{tamData.ab.total.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'écoles totales' : 'total schools'}</p>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Public:</span>
                        <span className="text-orange-300 font-semibold">{tamData.ab.public.toLocaleString('fr-CA')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">{language === 'fr' ? 'Privé' : 'Private'}:</span>
                        <span className="text-orange-300 font-semibold">{tamData.ab.prive}</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-orange-500/20">
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'Objectif' : 'Target'}</p>
                      <p className="text-xl font-bold text-orange-400">{objectifs.ab.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-orange-300">~{Math.round(objectifs.ab / tamData.ab.total * 100)}%</p>
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-gray-500">Population</p>
                      <p className="text-sm font-semibold text-gray-300">520k {language === 'fr' ? 'élèves' : 'students'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* COLOMBIE-BRITANNIQUE */}
                <Card className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-6 w-6 text-purple-400" />
                      <CardTitle className="text-white text-lg">{language === 'fr' ? 'C.-B.' : 'BC'}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-purple-400">{tamData.bc.total.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'écoles totales' : 'total schools'}</p>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Public:</span>
                        <span className="text-purple-300 font-semibold">{tamData.bc.public.toLocaleString('fr-CA')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">{language === 'fr' ? 'Privé' : 'Private'}:</span>
                        <span className="text-purple-300 font-semibold">{tamData.bc.prive}</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-purple-500/20">
                      <p className="text-xs text-gray-400">{language === 'fr' ? 'Objectif' : 'Target'}</p>
                      <p className="text-xl font-bold text-purple-400">{objectifs.bc.toLocaleString('fr-CA')}</p>
                      <p className="text-xs text-purple-300">~{Math.round(objectifs.bc / tamData.bc.total * 100)}%</p>
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-gray-500">Population</p>
                      <p className="text-sm font-semibold text-gray-300">450k {language === 'fr' ? 'élèves' : 'students'}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* 6 PROVINCES SECONDAIRES */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                {[
                  { key: 'mb', name: { fr: 'Manitoba', en: 'Manitoba' }, color: 'yellow', pop: '105k' },
                  { key: 'sk', name: { fr: 'Saskatchewan', en: 'Saskatchewan' }, color: 'teal', pop: '90k' },
                  { key: 'nb', name: { fr: 'N.-Brunswick', en: 'New Brunswick' }, color: 'rose', pop: '55k' },
                  { key: 'ne', name: { fr: 'N.-Écosse', en: 'Nova Scotia' }, color: 'blue', pop: '65k' },
                  { key: 'tnl', name: { fr: 'T.-N.-L.', en: 'NL' }, color: 'emerald', pop: '35k' },
                  { key: 'ipe', name: { fr: 'Î.-P.-É.', en: 'PEI' }, color: 'pink', pop: '12k' },
                ].map((prov) => (
                  <Card key={prov.key} className={`bg-gradient-to-br from-${prov.color}-500/20 to-${prov.color}-500/5 border-${prov.color}-500/30`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-1.5">
                        <Building2 className={`h-4 w-4 text-${prov.color}-400`} />
                        <p className="text-white text-sm font-semibold">{prov.name[language]}</p>
                      </div>
                      <p className={`text-2xl font-bold text-${prov.color}-400`}>{tamData[prov.key].total}</p>
                      <div className="text-xs space-y-0.5">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Pub:</span>
                          <span className={`text-${prov.color}-300 font-semibold`}>{tamData[prov.key].public}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">{language === 'fr' ? 'Priv' : 'Priv'}:</span>
                          <span className={`text-${prov.color}-300 font-semibold`}>{tamData[prov.key].prive}</span>
                        </div>
                      </div>
                      <div className="pt-1.5 border-t border-gray-700">
                        <p className="text-xs text-gray-400">{language === 'fr' ? 'Objectif' : 'Target'}</p>
                        <p className={`text-lg font-bold text-${prov.color}-400`}>{objectifs[prov.key]}</p>
                        <p className={`text-xs text-${prov.color}-300`}>~{Math.round(objectifs[prov.key] / tamData[prov.key].total * 100)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{prov.pop} {language === 'fr' ? 'élèves' : 'students'}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* 5ème CARTE - ÉCOLES PRIVÉES */}
              <div className="mt-4">
                <Card className="bg-gradient-to-br from-pink-500/20 via-pink-500/10 to-transparent border-pink-500/30 border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-6 w-6 text-pink-400" />
                      <CardTitle className="text-white text-lg">{language === 'fr' ? 'Écoles Privées (Multi-provinces)' : 'Private Schools (Multi-province)'}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-3 bg-black/30 rounded-lg">
                        <p className="text-xs text-gray-400">TAM Total</p>
                        <p className="text-2xl font-bold text-pink-400">1 575</p>
                        <p className="text-xs text-gray-500">{language === 'fr' ? 'écoles privées' : 'private schools'}</p>
                      </div>
                      <div className="text-center p-3 bg-black/30 rounded-lg border border-lime-500/20">
                        <p className="text-xs text-lime-400">QC</p>
                        <p className="text-lg font-bold text-lime-300">{tamData.qc.prive}</p>
                      </div>
                      <div className="text-center p-3 bg-black/30 rounded-lg border border-cyan-500/20">
                        <p className="text-xs text-cyan-400">ON</p>
                        <p className="text-lg font-bold text-cyan-300">{tamData.on.prive}</p>
                      </div>
                      <div className="text-center p-3 bg-black/30 rounded-lg border border-orange-500/20">
                        <p className="text-xs text-orange-400">AB</p>
                        <p className="text-lg font-bold text-orange-300">{tamData.ab.prive}</p>
                      </div>
                      <div className="text-center p-3 bg-black/30 rounded-lg border border-purple-500/20">
                        <p className="text-xs text-purple-400">BC</p>
                        <p className="text-lg font-bold text-purple-300">{tamData.bc.prive}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between p-4 bg-gradient-to-r from-pink-500/10 to-transparent rounded-lg border border-pink-500/30">
                      <div>
                        <p className="text-sm text-gray-400">{language === 'fr' ? 'Objectif Privé' : 'Private Target'}</p>
                        <p className="text-3xl font-bold text-pink-400">{objectifs.prive}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">{language === 'fr' ? 'Pénétration' : 'Penetration'}</p>
                        <p className="text-2xl font-bold text-pink-300">~{Math.round(objectifs.prive / 1575 * 100)}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">{language === 'fr' ? 'Stratégie' : 'Strategy'}</p>
                        <p className="text-xs text-pink-200">{language === 'fr' ? 'Premium + Flexibilité' : 'Premium + Flexibility'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Résumé Total */}
              <div className="mt-4 p-4 bg-gradient-to-r from-lime-500/10 via-cyan-500/10 to-purple-500/10 rounded-lg border border-lime-500/30">
                <div className="flex flex-wrap items-center justify-around gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'TAM Total' : 'Total TAM'}</p>
                    <p className="text-2xl font-bold text-white">{TAM_TOTAL.toLocaleString('fr-CA')}</p>
                    <p className="text-xs text-gray-500">{language === 'fr' ? 'écoles' : 'schools'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Objectif Public' : 'Public Target'}</p>
                    <p className="text-2xl font-bold text-cyan-400">{(objectifs.total - objectifs.prive).toLocaleString('fr-CA')}</p>
                    <p className="text-xs text-gray-500">{language === 'fr' ? 'écoles' : 'schools'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Objectif Privé' : 'Private Target'}</p>
                    <p className="text-2xl font-bold text-orange-400">{objectifs.prive}</p>
                    <p className="text-xs text-orange-300">~{Math.round(objectifs.prive / 1575 * 100)}% TAM</p>
                  </div>
                  <div className="border-l-2 border-lime-500/40 pl-4">
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'OBJECTIF TOTAL' : 'TOTAL TARGET'}</p>
                    <p className="text-3xl font-bold text-lime-400">{objectifs.total.toLocaleString('fr-CA')}</p>
                    <p className="text-xs text-lime-300">~{Math.round(objectifs.total / TAM_TOTAL * 100)}% TAM</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Population Totale' : 'Total Population'}</p>
                    <p className="text-2xl font-bold text-cyan-400">3.1M</p>
                    <p className="text-xs text-gray-500">{language === 'fr' ? 'élèves' : 'students'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Douleurs */}
            <div>
              <h3 className="text-xl font-bold text-orange-400 mb-4">{language === 'fr' ? 'Douleurs Identifiées' : 'Identified Pain Points'}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(language === 'fr' ? [
                  { label: 'Fragmentation des systèmes', icon: '🔀' },
                  { label: 'UX datée et complexe', icon: '⏳' },
                  { label: 'Double saisie des données', icon: '📋' },
                  { label: 'Faible gouvernance/qualité', icon: '⚠️' },
                  { label: 'Manque de rapports automatisés', icon: '📊' },
                  { label: 'Processus manuels inefficaces', icon: '✋' }
                ] : [
                  { label: 'System fragmentation', icon: '🔀' },
                  { label: 'Outdated and complex UX', icon: '⏳' },
                  { label: 'Double data entry', icon: '📋' },
                  { label: 'Weak governance/quality', icon: '⚠️' },
                  { label: 'Lack of automated reports', icon: '📊' },
                  { label: 'Inefficient manual processes', icon: '✋' }
                ]).map((pain, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-black/40 rounded-lg border border-orange-500/20">
                    <span className="text-2xl">{pain.icon}</span>
                    <span className="text-sm text-gray-300">{pain.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Structures Institutionnelles */}
            <div>
              <h3 className="text-xl font-bold text-cyan-400 mb-4">{language === 'fr' ? 'Structures Institutionnelles par Province' : 'Institutional Structures by Province'}</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Province' : 'Province'}</th>
                      <th className="text-left p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Structure' : 'Structure'}</th>
                      <th className="text-center p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Nombre d\'entités' : '# Entities'}</th>
                      <th className="text-center p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Moy. écoles/entité' : 'Avg schools/entity'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-800 hover:bg-lime-500/5">
                      <td className="p-3 text-lime-400 font-semibold">{language === 'fr' ? 'Québec' : 'Quebec'}</td>
                      <td className="p-3 text-gray-300">CSS</td>
                      <td className="p-3 text-center text-lime-300 font-semibold">~60</td>
                      <td className="p-3 text-center text-gray-300">~33</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-cyan-500/5">
                      <td className="p-3 text-cyan-400 font-semibold">Ontario</td>
                      <td className="p-3 text-gray-300">{language === 'fr' ? 'Conseils scolaires' : 'School Boards'}</td>
                      <td className="p-3 text-center text-cyan-300 font-semibold">72</td>
                      <td className="p-3 text-center text-gray-300">~55</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-orange-500/5">
                      <td className="p-3 text-orange-400 font-semibold">Alberta</td>
                      <td className="p-3 text-gray-300">School Divisions</td>
                      <td className="p-3 text-center text-orange-300 font-semibold">42</td>
                      <td className="p-3 text-center text-gray-300">~48</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-purple-500/5">
                      <td className="p-3 text-purple-400 font-semibold">{language === 'fr' ? 'Colombie-Britannique' : 'British Columbia'}</td>
                      <td className="p-3 text-gray-300">School Districts</td>
                      <td className="p-3 text-center text-purple-300 font-semibold">60</td>
                      <td className="p-3 text-center text-gray-300">~27</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-yellow-500/5">
                      <td className="p-3 text-yellow-400 font-semibold">Manitoba</td>
                      <td className="p-3 text-gray-300">School Divisions</td>
                      <td className="p-3 text-center text-yellow-300 font-semibold">37</td>
                      <td className="p-3 text-center text-gray-300">~14</td>
                    </tr>
                    <tr className="hover:bg-teal-500/5">
                      <td className="p-3 text-teal-400 font-semibold">Saskatchewan</td>
                      <td className="p-3 text-gray-300">School Divisions</td>
                      <td className="p-3 text-center text-teal-300 font-semibold">27</td>
                      <td className="p-3 text-center text-gray-300">~15</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                <p className="text-sm text-gray-300">
                  <span className="text-cyan-400 font-semibold">{language === 'fr' ? 'Stratégie :' : 'Strategy:'}</span> {language === 'fr' ? 'La signature d\'une seule entité institutionnelle donne accès à de multiples écoles simultanément, permettant une expansion rapide et structurée à travers les 10 provinces.' : 'Signing a single institutional entity provides access to multiple schools simultaneously, enabling rapid and structured expansion across all 10 provinces.'}
                </p>
              </div>
            </div>

            {/* Cibles B2B/B2C */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-lime-500/10 to-transparent border-lime-500/30">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-lime-400" />
                    <CardTitle className="text-white">{language === 'fr' ? 'Cibles B2B' : 'B2B Targets'}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-gray-300 flex items-center gap-2">
                    <span className="text-lime-500">→</span> {language === 'fr' ? 'Traiteurs' : 'Caterers'}
                  </p>
                  <p className="text-gray-300 flex items-center gap-2">
                    <span className="text-lime-500">→</span> {language === 'fr' ? 'Écoles' : 'Schools'}
                  </p>
                  <p className="text-gray-300 flex items-center gap-2">
                    <span className="text-lime-500">→</span> {language === 'fr' ? 'Centres de services scolaires (CSS)' : 'School service centers (CSS)'}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-cyan-500/10 to-transparent border-cyan-500/30">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-cyan-400" />
                    <CardTitle className="text-white">{language === 'fr' ? 'Cibles B2C' : 'B2C Targets'}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-gray-300 flex items-center gap-2">
                    <span className="text-cyan-500">→</span> {language === 'fr' ? 'Parents' : 'Parents'}
                  </p>
                  <p className="text-gray-300 flex items-center gap-2">
                    <span className="text-cyan-500">→</span> {language === 'fr' ? 'Enfants' : 'Children'}
                  </p>
                  <p className="text-gray-300 flex items-center gap-2">
                    <span className="text-cyan-500">→</span> {language === 'fr' ? 'Familles' : 'Families'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Opportunité */}
            <div className="p-6 bg-gradient-to-br from-orange-500/10 to-transparent rounded-xl border border-orange-500/30">
              <h3 className="text-xl font-bold text-orange-400 mb-3">{language === 'fr' ? 'Opportunité' : 'Opportunity'}</h3>
              <p className="text-gray-300 leading-relaxed">
                {language === 'fr' 
                  ? <>Marché sous-digitalisé avec forte demande pour solutions modernes et intégrées. <span className="text-orange-400 font-semibold">Aucun leader dominant</span> dans l'espace SaaS + FinTech pour repas scolaires (10 provinces canadiennes).</>
                  : <>Under-digitalized market with strong demand for modern and integrated solutions. <span className="text-orange-400 font-semibold">No dominant leader</span> in the SaaS + FinTech space for school meals (10 Canadian provinces).</>}
              </p>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
